// src/components/DeleteDialog/index.js

import React from "react";
import PropTypes from "prop-types";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { Warning, Close, Delete } from "@mui/icons-material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";

const DeleteDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = "Delete",
  cancelText = "Cancel",
  confirmIcon = <Delete />,
  confirmColor = "error",
  warningText = "This action cannot be undone.",
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "16px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          overflow: "visible",
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ pb: 1, position: "relative" }}>
        <ArgonBox display="flex" alignItems="center" gap={2}>
          <ArgonBox
            sx={{
              background: "linear-gradient(45deg, #ff6b6b, #ee5a52)",
              borderRadius: "12px",
              p: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 25px rgba(255, 107, 107, 0.3)",
            }}
          >
            <Warning sx={{ color: "white", fontSize: "24px" }} />
          </ArgonBox>
          <ArgonBox>
            <ArgonTypography variant="h5" color="text" fontWeight="medium">
              {title}
            </ArgonTypography>
            <ArgonTypography variant="body2" color="#6c757d">
              This action requires confirmation
            </ArgonTypography>
          </ArgonBox>
        </ArgonBox>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#6c757d",
            "&:hover": {
              backgroundColor: "#f8f9fa",
              color: "#dc3545",
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ py: 3 }}>
        <ArgonBox
          sx={{
            backgroundColor: "#eee8d2ff",
            border: "1px solid #ffeaa7",
            borderRadius: "8px",
            p: 2.5,
            mb: 2,
          }}
        >
          <ArgonTypography variant="body1" color="#856404" fontWeight="medium">
            {description}
          </ArgonTypography>
          <ArgonTypography variant="body2" color="text" mt={1}>
            {warningText}
          </ArgonTypography>
        </ArgonBox>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ p: 3, pt: 0, gap: 1.5, justifyContent: "flex-end" }}>
        <ArgonButton onClick={onClose} variant="outlined" color="primary" size="small" sx={{ minWidth: 120 }}>
          {cancelText}
        </ArgonButton>
        <ArgonButton
          onClick={onConfirm}
          variant="contained"
          size="small"
          startIcon={confirmIcon}
          color={confirmColor}
          sx={{ minWidth: 140 }}
        >
          {confirmText}
        </ArgonButton>
      </DialogActions>
    </Dialog>
  );
};

DeleteDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  confirmIcon: PropTypes.node,
  confirmColor: PropTypes.string,
  warningText: PropTypes.string,
};


export default DeleteDialog;
